import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, PauseCircle, PlayCircle, RotateCcw, Calendar, List, Bell } from 'lucide-react';
import './styles.css';

const POMODORO_MINUTES = 25;
const BREAK_MINUTES = 5;

const formatTime = (timeInSeconds) => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = timeInSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

function App() {
  // Today's date info
  const today = new Date();
  const formattedDate = today.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  // Get current time to show in the header
  const [currentTime, setCurrentTime] = useState(
    today.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    })
  );

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setCurrentTime(now.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      }));
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Tasks state
  const [tasks, setTasks] = useState([
    { id: 1, text: 'Email Bill/Sonny', completed: false, pomodoros: 1, category: 'work', deadline: 'Due: 8:30 AM' },
    { id: 2, text: 'Publish NP* posts (timely news)', completed: false, pomodoros: 2, category: 'studAIo', deadline: 'Due: 10:00 AM' },
    { id: 3, text: 'Update studAIo homepage', completed: false, pomodoros: 2, category: 'studAIo', deadline: 'Due: 12:00 PM' },
    { id: 4, text: 'Create social media profiles', completed: false, pomodoros: 3, category: 'studAIo', deadline: 'Due: 4:00 PM' },
    { id: 5, text: 'Find and follow accounts', completed: false, pomodoros: 1, category: 'studAIo', deadline: 'Due: 4:30 PM' },
    { id: 6, text: 'Send Brilliant project', completed: false, pomodoros: 2, category: 'work', deadline: 'Due: 5:30 PM' },
    { id: 7, text: 'Practice CodeSignal assessment', completed: false, pomodoros: 3, category: 'personal', deadline: 'Due: 7:00 PM' },
    { id: 8, text: 'Clean house & fold laundry', completed: false, pomodoros: 2, category: 'personal', deadline: 'Due: 8:00 PM' },
  ]);
  
  // Events/calendar state - removed setEvents since it's not used
  const [events] = useState([
    { id: 1, title: 'Blackman meeting', start: '8:30 AM', end: '9:00 AM', category: 'meeting' },
    { id: 2, title: 'ShayrdAir Product + Tech', start: '10:00 AM', end: '11:00 AM', category: 'meeting' },
    { id: 3, title: 'Gym session', start: '12:30 PM', end: '1:30 PM', category: 'personal' },
    { id: 4, title: 'Therapy session', start: '1:30 PM', end: '2:30 PM', category: 'personal' },
  ]);

  const [activeTask, setActiveTask] = useState(null);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(POMODORO_MINUTES * 60);
  const [isBreak, setIsBreak] = useState(false);
  const [completedPomodoros, setCompletedPomodoros] = useState(0);
  const [activeTab, setActiveTab] = useState('tasks');
  
  // Progress calculation
  const totalPomodoros = tasks.reduce((sum, task) => sum + task.pomodoros, 0);
  const completedTaskPomodoros = tasks
    .filter(task => task.completed)
    .reduce((sum, task) => sum + task.pomodoros, 0);
  const progressPercentage = Math.round((completedTaskPomodoros / totalPomodoros) * 100) || 0;
  
  useEffect(() => {
    let interval = null;
    
    if (timerRunning && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (timerRunning && timeLeft === 0) {
      // Timer completed
      if (!isBreak) {
        // Pomodoro completed, start break
        setCompletedPomodoros(completedPomodoros + 1);
        setIsBreak(true);
        setTimeLeft(BREAK_MINUTES * 60);
        // Play sound notification
        const audio = new Audio('https://assets.mixkit.co/sfx/preview/mixkit-software-interface-start-2574.mp3');
        audio.play();
      } else {
        // Break completed, reset for next pomodoro
        setIsBreak(false);
        setTimeLeft(POMODORO_MINUTES * 60);
        setTimerRunning(false);
        // Play sound notification
        const audio = new Audio('https://assets.mixkit.co/sfx/preview/mixkit-clear-announce-tones-2861.mp3');
        audio.play();
      }
    }
    
    return () => clearInterval(interval);
  }, [timerRunning, timeLeft, isBreak, completedPomodoros]);
  
  const startTimer = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    setActiveTask(task);
    setTimerRunning(true);
    setIsBreak(false);
    setTimeLeft(POMODORO_MINUTES * 60);
  };
  
  const pauseTimer = () => {
    setTimerRunning(false);
  };
  
  const resumeTimer = () => {
    setTimerRunning(true);
  };
  
  const resetTimer = () => {
    setTimerRunning(false);
    setTimeLeft(isBreak ? BREAK_MINUTES * 60 : POMODORO_MINUTES * 60);
  };
  
  const toggleTaskCompletion = (taskId) => {
    setTasks(tasks.map(task => 
      task.id === taskId ? { ...task, completed: !task.completed } : task
    ));
  };
  
  const categoryColors = {
    'work': 'bg-blue-100 text-blue-600',
    'studAIo': 'bg-purple-100 text-purple-600',
    'personal': 'bg-green-100 text-green-600',
    'meeting': 'bg-amber-100 text-amber-600'
  };

  return (
    <div className="app-container">
      {/* Header */}
      <div className="header">
        <div className="header-content">
          <div>
            <h1 className="app-title">Daily Focus</h1>
            <p className="date">{formattedDate}</p>
          </div>
          <div className="header-right">
            <p className="current-time">{currentTime}</p>
            <p className="progress">Progress: {progressPercentage}%</p>
          </div>
        </div>
      </div>
      
      {/* Timer Section */}
      <div className="timer-section">
        <div className="timer-container">
          <div className="timer-display">
            <div className="time-remaining">
              {formatTime(timeLeft)}
            </div>
            <div className="timer-status">
              {isBreak ? 'Break Time' : activeTask ? `Working on: ${activeTask.text}` : 'Select a task to start'}
            </div>
            <div className="timer-controls">
              {!timerRunning && activeTask && (
                <button onClick={resumeTimer} className="timer-button play">
                  <PlayCircle size={20} />
                </button>
              )}
              {timerRunning && (
                <button onClick={pauseTimer} className="timer-button pause">
                  <PauseCircle size={20} />
                </button>
              )}
              <button onClick={resetTimer} className="timer-button reset">
                <RotateCcw size={20} />
              </button>
            </div>
            <div className="pomodoro-count">
              <span>{completedPomodoros} pomodoros completed today</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="tabs">
        <button 
          onClick={() => setActiveTab('tasks')} 
          className={`tab ${activeTab === 'tasks' ? 'active' : ''}`}
        >
          <div className="tab-content">
            <List size={14} className="tab-icon" /> Tasks
          </div>
        </button>
        <button 
          onClick={() => setActiveTab('schedule')} 
          className={`tab ${activeTab === 'schedule' ? 'active' : ''}`}
        >
          <div className="tab-content">
            <Calendar size={14} className="tab-icon" /> Schedule
          </div>
        </button>
      </div>
      
      {/* Content area */}
      <div className="content-area">
        {activeTab === 'tasks' && (
          <div className="tasks-container">
            <h2 className="section-title">Today's Tasks</h2>
            <div className="task-list">
              {tasks.map(task => (
                <div key={task.id} className="task-item">
                  <div className="task-content">
                    <div className="task-header">
                      <button 
                        onClick={() => toggleTaskCompletion(task.id)}
                        className={`checkbox ${task.completed ? 'completed' : ''}`}
                      >
                        <CheckCircle size={20} />
                      </button>
                      <span className={task.completed ? 'completed-text' : ''}>
                        {task.text}
                      </span>
                    </div>
                    <div className="task-details">
                      <span className={`category-badge ${categoryColors[task.category]}`}>
                        {task.category}
                      </span>
                      <span className="deadline">{task.deadline}</span>
                    </div>
                  </div>
                  <div className="task-actions">
                    <div className="pomodoro-estimate">
                      {task.pomodoros} × <Clock size={12} className="pomodoro-icon" />
                    </div>
                    {!task.completed && (
                      <button 
                        onClick={() => startTimer(task.id)}
                        className="start-task-button"
                      >
                        <PlayCircle size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {activeTab === 'schedule' && (
          <div className="schedule-container">
            <h2 className="section-title">Today's Schedule</h2>
            <div className="event-list">
              {events.map(event => (
                <div key={event.id} className="event-item">
                  <div className="event-content">
                    <h3 className="event-title">{event.title}</h3>
                    <div className="event-details">
                      <span className={`category-badge ${categoryColors[event.category]}`}>
                        {event.category}
                      </span>
                      <span className="event-time">{event.start} - {event.end}</span>
                    </div>
                  </div>
                  <div className="event-actions">
                    <Bell size={16} className="notification-icon" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="footer">
        <span>Synced with Supabase</span>
        <span>Last updated: {new Date().toLocaleTimeString()}</span>
      </div>
    </div>
  );
}

export default App;